import { normalizeOnboardingStages } from "@/lib/onboardingPlan";
import { resolveLeadGenerationAnswer, type LeadGenerationFields } from "@/lib/orgLeadGeneration";
import { normalizeSupport } from "@/lib/orgSupport";
import { EMPTY_CATALOG_INDEX, normalizeToolStack } from "@/lib/toolStack";
import { STORY_SECTION_ORDER, type OrgStoryBase, type StorySectionKey, type StoryNews } from "@/lib/orgStory/types";

/**
 * Whether the public RPC's newsroom payload has anything to show: at least
 * one article or one award. The single source of truth for public News
 * presence — used here to decide the section's pill/order, and by
 * `PublicNewsSection` to decide whether it renders, so the two can never
 * disagree about an empty newsroom.
 */
export function hasPublicNews(news: StoryNews | null): boolean {
  return !!news && (news.articles.length > 0 || news.awards.length > 0);
}

/**
 * THE rule for which story sections render (spec 2026-09-24 §5.1, §6.1): an
 * empty section does not render and has no pill. Components never re-decide
 * it. Presence means "there is something to show", so an explicit `false`
 * (no recurring fees: no) is an answer but not, on its own, content.
 *
 * SELF-FETCHING SECTIONS. For a signed-in viewer, Team, Reviews and News load
 * their own data (get_org_team_members_public, get_org_reviews, the newsroom
 * tables), so the adapter cannot know whether they are empty. They are listed
 * here and hide themselves when their fetch comes back empty (PR 3). The
 * public page has no team roster or reviews by rule, and its news is in the
 * RPC payload, so its News is decided here.
 */

type Pd = Record<string, unknown>;

const text = (v: unknown): boolean => typeof v === "string" && v.trim() !== "";
const filled = (v: unknown): boolean => text(v) || (typeof v === "number" && Number.isFinite(v));
const nonEmpty = (v: unknown): boolean => Array.isArray(v) && v.length > 0;
const yes = (v: unknown): boolean => v === true;
const obj = (v: unknown): Pd =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Pd) : {};
const anyFilled = (o: Pd, keys: string[]): boolean => keys.some((k) => filled(o[k]));

/**
 * A lead stat (leads per month, conversion rate) counts as content only when
 * it is a non-zero figure. DELIBERATELY stricter than ProfileDataValueProp,
 * which would render a bare "0" or "0%": a zero on its own is not worth
 * opening the Offer section for. It only ever hides a section, never shows
 * an empty one.
 */
const statValue = (v: unknown): boolean =>
  (typeof v === "string" && v.trim() !== "" && v.trim() !== "0") ||
  (typeof v === "number" && Number.isFinite(v) && v !== 0);

/** Some entry in a list has a non-blank name/title, whether it's a bare string or `{ [field]: string }`. */
const someNamed = (v: unknown, field: string): boolean =>
  Array.isArray(v) && v.some((entry) => text(entry) || text(obj(entry)[field]));

const hasPerformance = (raw: unknown): boolean => {
  const perf = obj(raw);
  const presence = obj(perf.marketPresence);
  const rankings = Array.isArray(perf.rankings) ? perf.rankings : [];
  const quarterlyTrend = Array.isArray(perf.quarterlyTrend) ? perf.quarterlyTrend : [];
  return (
    anyFilled(obj(perf.productionMetrics), ["totalVolume", "totalTransactions", "avgSalePrice", "volumeGrowthYoY"]) ||
    anyFilled(obj(perf.agentPerformance), ["retentionRate", "avgTenure", "newAgentSuccessRate", "incomeGoalHitRate"]) ||
    rankings.some((r) => filled(obj(r).rank) && text(obj(r).category)) ||
    anyFilled(presence, ["marketShare", "daysOnMarket", "listToSaleRatio"]) ||
    nonEmpty(presence.specializations) ||
    nonEmpty(perf.incomeDistribution) ||
    nonEmpty(perf.growthTimeline) ||
    quarterlyTrend.some((q) => !!obj(q).label && !!obj(q).volume)
  );
};

const PRESENCE: Record<StorySectionKey, (v: OrgStoryBase) => boolean> = {
  overview: ({ profileData: pd, agentCount, identity }) =>
    text(pd.missionStatement) ||
    text(pd.ourStory) ||
    text(pd.whatMakesUsDifferent) ||
    text(obj(pd.featuredQuote).text) ||
    nonEmpty(pd.byTheNumbers) ||
    text(pd.brandVideoUrl) ||
    nonEmpty(pd.galleryPhotos) ||
    agentCount != null ||
    (typeof identity.foundedYear === "number" && identity.foundedYear < new Date().getFullYear()),

  offer: ({ profileData: pd }) =>
    // primaryCompensationModel is NOT content on its own: ProfileDataValueProp
    // renders it only alongside compensationModels, and alone it would show
    // the "shared directly" empty card.
    nonEmpty(pd.compensationModels) ||
    text(pd.compensationPhilosophy) ||
    nonEmpty(pd.feeTypes) ||
    yes(pd.noRecurringFees) ||
    text(pd.feeNotes) ||
    resolveLeadGenerationAnswer(pd as LeadGenerationFields) != null ||
    someNamed(pd.leadSourcesStructured, "name") ||
    nonEmpty(pd.leadSources) ||
    text(pd.leadVolume) ||
    statValue(pd.leadsPerMonth) ||
    statValue(pd.conversionRate) ||
    nonEmpty(pd.selectedBenefits) ||
    yes(pd.hasHealthInsurance) ||
    yes(pd.hasRetirementPlan) ||
    nonEmpty(pd.officePerks) ||
    text(pd.ptoPolicy),

  growth: ({ profileData: pd }) =>
    normalizeOnboardingStages(pd.onboardingStages).length > 0 ||
    someNamed(pd.trainingPrograms, "name") ||
    nonEmpty(pd.trainingFrequency) ||
    text(pd.trainingFrequency) ||
    yes(pd.oneOnOneCoaching) ||
    yes(pd.hasTrainingLibrary) ||
    someNamed(pd.careerStages, "title") ||
    text(pd.careerPathDescription) ||
    yes(pd.partnershipOpportunities) ||
    text(pd.partnershipDetails),

  // The same normalizers OperationsSection renders from (#958), so priced or
  // malformed entries that the section drops never earn a pill. Emptiness does
  // not depend on the catalog, so the empty index is enough here.
  operations: ({ profileData: pd }) =>
    !normalizeToolStack(pd.technologyTools, EMPTY_CATALOG_INDEX).isEmpty ||
    !normalizeSupport(pd).isEmpty,

  footprint: ({ officeLabels, serviceTypes, profileData: pd }) =>
    officeLabels.length > 0 || serviceTypes.length > 0 || hasPerformance(pd.performanceData),

  team: ({ audience }) => audience === "signedIn",
  reviews: ({ audience }) => audience === "signedIn",
  news: ({ audience, news }) => audience === "signedIn" || hasPublicNews(news),
};

export function storySections(base: OrgStoryBase): StorySectionKey[] {
  return STORY_SECTION_ORDER.filter((key) => PRESENCE[key](base));
}
